/**
 * InterestTagger — free-text → structured tag suggester (Feb 2026).
 *
 * Drops onto any pharma_contact or company_profile detail page. User
 * types free text ("gastrointestinal health and immune system"), clicks
 * Analyze, reviews the AI-suggested structured tags with checkboxes,
 * approves selected ones → tags merge into the entity's profile fields
 * (interest_areas, preferred_modalities, preferred_stages,
 * geographic_interests, excluded_areas, notes).
 *
 * The AI does NOT auto-apply. All suggestions are shown for approval.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_LABEL = {
  interest_areas: 'Therapeutic Areas',
  therapeutic_areas_specific: 'Specific Indications',
  preferred_modalities: 'Preferred Modalities',
  excluded_modalities: 'Excluded Modalities',
  preferred_stages: 'Preferred Stages',
  geographic_interests: 'Geographies',
  excluded_areas: 'Excluded Areas',
  excluded_indications: 'Excluded Indications',
  scientific_preferences: 'Scientific Preferences',
  focus_notes: 'Focus Notes',
};

const FIELD_STYLE = {
  interest_areas: 'border-violet-300 bg-violet-50 text-violet-700',
  therapeutic_areas_specific: 'border-indigo-300 bg-indigo-50 text-indigo-700',
  preferred_modalities: 'border-sky-300 bg-sky-50 text-sky-700',
  excluded_modalities: 'border-rose-300 bg-rose-50 text-rose-700',
  preferred_stages: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  geographic_interests: 'border-amber-300 bg-amber-50 text-amber-700',
  excluded_areas: 'border-rose-300 bg-rose-50 text-rose-700',
  excluded_indications: 'border-rose-300 bg-rose-50 text-rose-700',
  scientific_preferences: 'border-teal-300 bg-teal-50 text-teal-700',
  focus_notes: 'border-slate-300 bg-slate-50 text-slate-700',
};

export const InterestTagger = ({ entityType, entityId, onApplied, initialText = '' }) => {
  const [text, setText] = useState(initialText);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [selected, setSelected] = useState({});

  const analyze = async () => {
    if (!text.trim()) { toast.error('Enter some interest text first'); return; }
    setAnalyzing(true);
    try {
      const r = await axios.post(`${API}/api/interest-tagger/suggest`,
        { text }, { withCredentials: true });
      const s = r.data.suggestions || {};
      setSuggestions(s);
      // Auto-select every suggested tag; user can uncheck ones they don't want.
      const pre = {};
      Object.entries(s).forEach(([field, vals]) => {
        (vals || []).forEach((v) => { pre[`${field}::${v}`] = true; });
      });
      setSelected(pre);
      const totalCount = Object.values(s).reduce((n, arr) => n + (arr?.length || 0), 0);
      if (totalCount === 0) {
        toast.warning('No structured tags could be inferred from that text.');
      } else {
        toast.success(`${totalCount} tag(s) suggested — review and approve`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analyze failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const apply = async () => {
    if (!suggestions) return;
    // Build tags payload from checked boxes.
    const tags = {};
    Object.keys(FIELD_LABEL).forEach((f) => {
      const picked = (suggestions[f] || []).filter((v) => selected[`${f}::${v}`]);
      if (picked.length) tags[f] = picked;
    });
    const totalPicked = Object.values(tags).reduce((n, arr) => n + arr.length, 0);
    if (!totalPicked) { toast.error('Select at least one tag to apply'); return; }
    setApplying(true);
    try {
      const r = await axios.post(`${API}/api/interest-tagger/apply`, {
        entity_type: entityType, entity_id: entityId,
        tags, raw_notes: text,
      }, { withCredentials: true });
      if (r.data.updated) {
        toast.success(`Applied ${r.data.added} new tag(s) to profile`);
        setSuggestions(null);
        setSelected({});
        onApplied && onApplied();
      } else {
        toast.info(r.data.message || 'Nothing new to apply — profile already had these tags.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2" data-testid="interest-tagger">
      <div className="flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-violet-600" />
        <span className="text-xs font-semibold text-violet-800">Known Interests · AI Tagger</span>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="text-xs min-h-[54px] bg-white/80 border-violet-200"
        placeholder="e.g. 'HER2+ breast cancer and NSCLC ADCs, no radiopharma, prefer novel first-in-class, Phase 2+' — the AI extracts specific tumors, modalities, exclusions and scientific preferences."
        data-testid="interest-tagger-input"
      />
      <div className="flex gap-2">
        <Button
          variant="outline" size="sm"
          className="h-7 text-[11px] border-violet-300 text-violet-700 hover:bg-violet-100"
          onClick={analyze} disabled={analyzing || !text.trim()}
          data-testid="interest-tagger-analyze"
        >
          {analyzing ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Analyzing…</> : <><Sparkles className="w-3 h-3 mr-1" /> Analyze with AI</>}
        </Button>
      </div>
      {suggestions && (
        <div className="space-y-2 pt-2 border-t border-violet-200">
          <div className="text-[11px] font-semibold text-slate-700">Suggested tags — review and approve:</div>
          {Object.keys(FIELD_LABEL).map((field) => {
            const vals = suggestions[field] || [];
            if (!vals.length) return null;
            return (
              <div key={field}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{FIELD_LABEL[field]}</div>
                <div className="flex flex-wrap gap-1.5">
                  {vals.map((v) => {
                    const key = `${field}::${v}`;
                    const isOn = !!selected[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelected((s) => ({ ...s, [key]: !s[key] }))}
                        className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${isOn ? FIELD_STYLE[field] : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50'}`}
                        data-testid={`interest-tagger-chip-${field}-${v}`.replace(/\s+/g, '-')}
                      >
                        {isOn ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="flex gap-2 pt-1">
            <Button size="sm"
              className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700 text-white"
              onClick={apply} disabled={applying}
              data-testid="interest-tagger-apply"
            >
              {applying ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Applying…</> : <><Check className="w-3 h-3 mr-1" /> Apply Selected Tags</>}
            </Button>
            <Button size="sm" variant="ghost"
              className="h-7 text-[11px] text-slate-500"
              onClick={() => { setSuggestions(null); setSelected({}); }}
              data-testid="interest-tagger-cancel"
            >
              Discard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterestTagger;
